// src/routes/privacy.tsx
import { createFileRoute } from '@tanstack/react-router';
import { createRouteConfig } from '../utils/routeGuard';

// adjust these import paths to where you placed the components
import { LegalLayout } from '@/components/LegalLayout';
import { LegalSection } from '@/components/LegalSection';

// --- SEO copied from your existing privacy page ---
export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
  ...createRouteConfig({
    isPublic: true,
    seo: {
      title: 'Privacy Policy - Trackplicant',
      description:
        'Privacy policy for Trackplicant, the AI-powered job application tracking platform.',
      ogTitle: 'Privacy Policy - Trackplicant',
      ogDescription:
        'Read our privacy policy to understand how we protect your data on Trackplicant.',
    },
  }),
});

function PrivacyPage() {
  return (
    <LegalLayout title='Privacy Policy'>
      {/* Intro block (excluded from TOC because it has no `title` prop) */}
      <div className='text-sm text-gray-600'>Last updated: Jul 27, 2025</div>
      <p className='mt-2 text-gray-700'>
        Trackplicant (“we”, “our”, or “us”) provides a job application tracking
        service to help individuals manage their job search more effectively.
        This Privacy Policy explains how we collect, use, and protect your
        information when you use our website, mobile apps, and services
        (collectively, the “Service”).
      </p>

      <LegalSection title='Information We Collect'>
        <div className='space-y-4'>
          <div>
            <p className='font-semibold'>Personal Information</p>
            <p>We may collect personal information you provide, such as:</p>
            <ul className='list-disc ml-5 space-y-1'>
              <li>Name</li>
              <li>Email address</li>
              <li>Job application details (e.g., company, role, status)</li>
              <li>Resume data or notes you upload</li>
            </ul>
          </div>

          <div>
            <p className='font-semibold'>Usage Data</p>
            <p>
              We automatically collect usage data to improve our services,
              including:
            </p>
            <ul className='list-disc ml-5 space-y-1'>
              <li>Device type and OS version</li>
              <li>App interactions and screen views</li>
              <li>IP address and general location (not precise)</li>
              <li>Crash reports and diagnostic data</li>
            </ul>
          </div>

          <div>
            <p className='font-semibold'>Third-Party Analytics</p>
            <p>
              We use tools like Sentry and PostHog to collect anonymized
              diagnostic and usage information. This helps us improve stability,
              performance, and user experience.
            </p>
          </div>
        </div>
      </LegalSection>

      <LegalSection title='Legal Basis for Processing (UK & Canada)'>
        <p>
          If you are located in the United Kingdom or Canada, we process your
          personal data under the legal bases outlined in the UK General Data
          Protection Regulation (UK GDPR) or the Personal Information Protection
          and Electronic Documents Act (PIPEDA), as applicable. These legal
          bases include:
        </p>
        <ul className='list-disc ml-5 space-y-1 mt-2'>
          <li>Your consent</li>
          <li>The necessity of processing to perform a contract</li>
          <li>
            Our legitimate interests in operating and improving the Service
          </li>
        </ul>
      </LegalSection>

      <LegalSection title='How We Use Your Information'>
        <p>We use the information we collect to:</p>
        <ul className='list-disc ml-5 space-y-1 mt-2'>
          <li>Provide and maintain the Trackplicant service</li>
          <li>Improve our app and website functionality</li>
          <li>Send important service-related notifications</li>
          <li>Respond to user support requests</li>
          <li>Analyze aggregate usage trends</li>
        </ul>
      </LegalSection>

      <LegalSection title='How We Share Your Information'>
        <p className='mb-2'>
          We <strong>do not sell or rent</strong> your personal information.
        </p>
        <p>We may share your information with:</p>
        <ul className='list-disc ml-5 space-y-1 mt-2'>
          <li>
            <strong>Service providers</strong> who help us operate the platform
            (e.g., cloud hosting, analytics)
          </li>
          <li>
            <strong>Law enforcement or regulators</strong> if legally required
          </li>
          <li>
            <strong>New owners</strong> in the event of a merger, sale, or
            acquisition
          </li>
        </ul>
        <p className='mt-2'>
          All third parties are required to handle your data in accordance with
          applicable privacy laws.
        </p>
      </LegalSection>

      <LegalSection title='Your Rights'>
        <div className='space-y-4'>
          <div>
            <p className='font-semibold'>General Rights</p>
            <p>
              You can access and update your account information at any time by
              visiting your account page. You may also opt out of marketing
              emails via unsubscribe links. Transactional emails (e.g., password
              reset, subscription reminders) will still be sent as needed.
            </p>
          </div>

          <div>
            <p className='font-semibold'>UK &amp; Canada-Specific Rights</p>
            <p>
              If you are located in the UK or Canada, you may have the following
              rights:
            </p>
            <ul className='list-disc ml-5 space-y-1 mt-2'>
              <li>The right to access the personal data we hold about you</li>
              <li>The right to request correction or deletion of your data</li>
              <li>
                The right to object to or restrict certain types of data
                processing
              </li>
              <li>The right to data portability</li>
              <li>
                The right to lodge a complaint with a supervisory authority (UK)
                or the Office of the Privacy Commissioner of Canada (Canada)
              </li>
            </ul>
            <p className='mt-2'>
              To exercise any of these rights, please contact us at{' '}
              <a
                className='text-blue-600 underline hover:text-blue-800'
                href='mailto:support@trackplicant.com'
              >
                support@trackplicant.com
              </a>
              .
            </p>
          </div>
        </div>
      </LegalSection>

      <LegalSection title='Data Security'>
        <p>
          We use reasonable technical and organizational measures to protect
          your data. However, no system is 100% secure. You use the Service at
          your own risk.
        </p>
      </LegalSection>

      <LegalSection title='Children’s Privacy'>
        <p>
          Trackplicant is not intended for children under 16, and we do not
          knowingly collect personal information from them.
        </p>
      </LegalSection>

      <LegalSection title='International Data Transfers'>
        <p className='mb-2'>
          Trackplicant is operated in the United States and United Kingdom. If
          you are located in Canada or another country outside these regions,
          please be aware that your personal information may be transferred to,
          stored, and processed in the United States or United Kingdom, where
          our servers and service providers are located.
        </p>
        <p>
          We take steps to ensure that data transfers comply with applicable
          data protection laws and that your data is protected through
          contractual safeguards or adequacy decisions (e.g., the UK recognizes
          Canadian data protection laws as adequate).
        </p>
      </LegalSection>

      <LegalSection title='Changes to This Policy'>
        <p>
          We may update this Privacy Policy from time to time. The latest
          version will always be available at{' '}
          <a
            className='text-blue-600 underline hover:text-blue-800'
            href='https://trackplicant.com/privacy'
            target='_blank'
            rel='noopener noreferrer'
          >
            https://trackplicant.com/privacy
          </a>
          . Your continued use of the Service constitutes acceptance of any
          changes.
        </p>
      </LegalSection>

      <LegalSection title='Contact Us'>
        <p>
          If you have any questions or concerns about this Privacy Policy or our
          data practices, please contact us at:
        </p>
        <div className='mt-2 space-y-1'>
          <div>
            <strong>Trackplicant</strong>
          </div>
          <div>
            <a
              className='text-blue-600 underline hover:text-blue-800'
              href='mailto:privacy@trackplicant.com'
            >
              privacy@trackplicant.com
            </a>
          </div>
          <div>
            <a
              className='text-blue-600 underline hover:text-blue-800'
              href='https://trackplicant.com'
              target='_blank'
              rel='noopener noreferrer'
            >
              https://trackplicant.com
            </a>
          </div>
        </div>
      </LegalSection>

      <LegalSection title='Supervisory Authorities'>
        <ul className='list-disc ml-5 space-y-1'>
          <li>
            UK:&nbsp;
            <a
              className='text-blue-600 underline hover:text-blue-800'
              href='https://ico.org.uk/'
              target='_blank'
              rel='noopener noreferrer'
            >
              Information Commissioner’s Office (ICO)
            </a>
          </li>
          <li>
            Canada:&nbsp;
            <a
              className='text-blue-600 underline hover:text-blue-800'
              href='https://www.priv.gc.ca/'
              target='_blank'
              rel='noopener noreferrer'
            >
              Office of the Privacy Commissioner of Canada
            </a>
          </li>
        </ul>
      </LegalSection>
    </LegalLayout>
  );
}
